
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ROLE_ROUTES } from "@utils/constants";
import {
  setSelectedAssignedFolderId,
  setSelectedAssignedProjectId,
} from "@store/slices/translatorAdminSlice";
import {
  setSelectedAssignedFolderId as setValidatorFolderId,
  setSelectedAssignedProjectId as setValidatorProjectId,
} from "@store/slices/validatorAdminSlice";
import {
  fetchAllInProgressProjects,
  fetchInProgressFiles,
  fetchInProgressFolders,
} from "@store/thunks/translationThunk";
import {
  fetchValidatorAdminProjects,
  fetchValidatorAdminFiles,
  fetchValidatorAdminFolders,
} from "@store/thunks/validatorAdminStructureThunk";

const SideBarCommon = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { role, user } = useSelector((store) => store.auth);
  const isValidatorAdmin = role === "VALIDATOR_VENDOR_ADMIN";

  // Get the correct slice based on role
  const {
    assignedProjects = [],
    assignedFolders = {},
    assignedFiles = {},
    selectedAssignedProjectId,
    selectedAssignedFolderId,
  } = useSelector((store) =>
    isValidatorAdmin ? store.validatorAdmin : store.translatorAdmin
  );

  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        if (isValidatorAdmin && user) {
          const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;
          const validatorAdminId = parsedUser?.userRegistrationDetailsId;
          if (validatorAdminId) {
            await dispatch(fetchValidatorAdminProjects(validatorAdminId));
          }
        } else {
          await dispatch(fetchAllInProgressProjects());
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchProjects();
  }, [dispatch, isValidatorAdmin, user]);

  useEffect(() => {
    if (location.pathname === `${destinationRoute}`) {
      if (isValidatorAdmin) {
        dispatch(setValidatorProjectId(null));
      } else {
        dispatch(setSelectedAssignedProjectId(null));
      }
    }
  }, [location.pathname, destinationRoute, dispatch, isValidatorAdmin]);

  // New useEffect to highlight the first project on page reload
  useEffect(() => {
    const safeAssignedProjects = Array.isArray(assignedProjects) ? assignedProjects : [];
    if (
      safeAssignedProjects.length > 0 &&
      !selectedAssignedProjectId &&
      location.pathname === `${destinationRoute}`
    ) {
      const firstProjectId = safeAssignedProjects[0].projectId;
      if (isValidatorAdmin) {
        dispatch(setValidatorProjectId(firstProjectId));
      } else {
        dispatch(setSelectedAssignedProjectId(firstProjectId));
      }
    }
  }, [assignedProjects, selectedAssignedProjectId, location.pathname, destinationRoute, dispatch, isValidatorAdmin]);

  const handleProjectClick = async (projectId) => {
    try {
      if (isValidatorAdmin) {
        if (selectedAssignedProjectId === projectId) {
          dispatch(setValidatorProjectId(projectId));
        } else {
          dispatch(setValidatorProjectId(projectId));
          if (!assignedFolders[projectId]) {
            console.log(user)
            const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;
            const validatorAdminId = parsedUser?.userRegistrationDetailsId;
            if (validatorAdminId) {
              await dispatch(fetchValidatorAdminFolders(validatorAdminId, projectId));
            }
          }
        }
      } else {
        if (selectedAssignedProjectId === projectId) {
          dispatch(setSelectedAssignedProjectId(projectId));
        } else {
          dispatch(setSelectedAssignedProjectId(projectId));
          if (!assignedFolders[projectId]) {
            await dispatch(fetchInProgressFolders(projectId));
          }
        }
      }
      navigate(`${destinationRoute}/folders`);
    } catch (error) {
      console.error("Error handling project click:", error);
    }
  };

  const handleFolderClick = async (folderId) => {
    try {
      if (isValidatorAdmin) {
        if (selectedAssignedFolderId === folderId) {
          dispatch(setValidatorFolderId(folderId));
        } else {
          dispatch(setValidatorFolderId(folderId));
          if (!assignedFiles[folderId]) {
            const parsedUser = typeof user === 'string' ? JSON.parse(user) : user;
            const validatorAdminId = parsedUser?.userRegistrationDetailsId;
            if (validatorAdminId) {
              await dispatch(fetchValidatorAdminFiles(validatorAdminId, folderId));
            }
          }
        }
      } else {
        if (selectedAssignedFolderId === folderId) {
          dispatch(setSelectedAssignedFolderId(folderId));
        } else {
          dispatch(setSelectedAssignedFolderId(folderId));
          if (!assignedFiles[folderId]) {
            await dispatch(fetchInProgressFiles(folderId));
          }
        }
      }
      navigate(`${destinationRoute}/folders/files`);
    } catch (error) {
      console.error("Error handling folder click:", error);
    }
  };

  if (!user) {
    return (
      <div className="p-4 sm:p-6 text-gray-500 text-sm font-['Semantic/Strong']">
        Loading user information...
      </div>
    );
  }

  // Safe data access functions
  const getSafeAssignedProjects = () => {
    return Array.isArray(assignedProjects) ? assignedProjects : [];
  };

  const getSafeAssignedFolders = (projectId) => {
    const folders = assignedFolders[projectId];
    return Array.isArray(folders) ? folders : [];
  };

  const safeAssignedProjects = getSafeAssignedProjects();

  return (
    <div className="bg-white border-r border-gray-200 shadow-sm h-screen overflow-y-auto w-full mt-12">
      <div className="p-4 sm:p-6">
        <h3 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900 mb-4">
          Projects & Folders
        </h3>
        {safeAssignedProjects.length > 0 ? (
          safeAssignedProjects.map((project) => {
            const isSelected = selectedAssignedProjectId === project.projectId;
            const foldersForProject = assignedFolders[project.projectId];
            const foldersHaveBeenFetched = Array.isArray(foldersForProject);

            return (
              <div key={project.projectId} className="mb-3">
                {/* Project Level */}
                <div
                  className={`cursor-pointer p-2 rounded-lg text-sm font-['Semantic/Strong'] transition-colors duration-200 ${isSelected ? "bg-[#ec7026] text-white" : "bg-gray-50 text-gray-700 hover:bg-gray-100"}`}
                  onClick={() => handleProjectClick(project.projectId)}
                >
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <span className="truncate">{project.projectName}</span>
                  </div>
                </div>

                {/* Conditionally render folders ONLY if they have been fetched and are not empty */}
                {isSelected && foldersHaveBeenFetched && foldersForProject.length > 0 && (
                  <div className="ml-4 mt-2 space-y-1">
                    {foldersForProject.map((folder) => (
                      <div
                        key={folder.folderId}
                        className={`cursor-pointer p-2 rounded-lg text-sm font-['Semantic/Strong'] transition-colors duration-200 ${selectedAssignedFolderId === folder.folderId ? "bg-[#ec7026]/10 text-[#ec7026]" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                        onClick={() => handleFolderClick(folder.folderId)}
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="truncate">{folder.folderName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Conditionally render "No folders" message ONLY if they have been fetched and the result is an empty array */}
                {isSelected && foldersHaveBeenFetched && foldersForProject.length === 0 && (
                  <div className="ml-4 mt-2 p-2 text-gray-500 text-sm font-['Semantic/Strong']">
                    No folders available
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-gray-500 text-sm font-['Semantic/Strong']">
            No projects available
          </div>
        )}
      </div>
    </div>
  );
};

export default SideBarCommon;